import * as React from 'react';
// eslint-disable-next-line no-restricted-imports
import EsriConfig from '@arcgis/core/config';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { EsriAlternativeBasemapsIds } from './configurations';
import { createEsriErrorInterceptor } from './esri-identity';
import { EsriIdentityContext } from './esri-identity-context';
import { useEsriIdentity } from './hooks/use-esri-identity';
import { clearMapTileCache, startMapTileCache } from './helpers/map-tile-cache';

function removeInterceptor(interceptor: __esri.RequestInterceptor | undefined) {
    const interceptors = EsriConfig.request.interceptors;
    if (!interceptor || !Array.isArray(interceptors)) {
        return;
    }
    const index = interceptors.indexOf(interceptor);
    if (index !== -1) {
        interceptors.splice(index, 1);
    }
}

/**
 * Component which will set required Esri configuration globally, via the config singleton provided by Esri's SDK.
 */
export function EsriConfiguration({
    children,
}: React.PropsWithChildren<Record<never, never>>): JSX.Element {
    const state = useBootstrapState();
    const { esriOauthTokenEnabled, mapTileCacheEnabled } = useEnabledFeatures();

    const identity = useEsriIdentity({ enabled: esriOauthTokenEnabled, apiKey: state.esriApiKey });

    React.useEffect(() => {
        // https://developers.arcgis.com/javascript/latest/api-reference/esri-config.html#RequestInterceptor
        if (!Array.isArray(EsriConfig.request.interceptors)) {
            return;
        }

        const errorInterceptor = createEsriErrorInterceptor({
            urls: [
                // 'dark-gray' and 'imagery' base maps
                'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/webmaps/arcgis',
                // alternative dark base map
                `https://www.arcgis.com/sharing/rest/content/items/${EsriAlternativeBasemapsIds.DARK}/data`,
                // alternative satellite base map
                `https://www.arcgis.com/sharing/rest/content/items/${EsriAlternativeBasemapsIds.SATELLITE}/data`,
                // web portal
                'https://www.arcgis.com/sharing/rest/content/items/webmapPortalId',
                // if any basemap or webmap portal requests failed. 'sharing/rest/portals/self' will also fail. But there may be other reasons that cause this request to fail. Monitor both for now.
                'https://www.arcgis.com/sharing/rest/portals/self',
            ],
            onTokenError: identity.refreshCredential,
        });
        // The SDK runs only the first interceptor whose `urls` match a request, and the cache
        // covers the basemap styles URLs above. So the cache goes first and passes failures on
        // those URLs to the error interceptor.
        const mapTileCache = mapTileCacheEnabled
            ? startMapTileCache({ delegateErrorsTo: errorInterceptor })
            : undefined;
        if (mapTileCache) {
            EsriConfig.request.interceptors.unshift(mapTileCache.interceptor);
        }
        EsriConfig.request.interceptors.push(errorInterceptor);

        return () => {
            mapTileCache?.stop();
            removeInterceptor(mapTileCache?.interceptor);
            removeInterceptor(errorInterceptor);
        };
    }, [identity.refreshCredential, mapTileCacheEnabled]);

    React.useEffect(
        function clearMapTileCacheWhenTurnedOff() {
            // Strictly false. Undefined means there is no feature map to read, not that a
            // department turned the cache off.
            if (mapTileCacheEnabled !== false) {
                return;
            }
            // Housekeeping that must never reach the map. clearMapTileCache already handles
            // its own failures; these are a second net, for a rejection or a throw.
            try {
                clearMapTileCache().catch(() => undefined);
            } catch {
                // Nothing to do: the cache stays on disk until the next app load tries again.
            }
        },
        [mapTileCacheEnabled]
    );

    return <EsriIdentityContext.Provider value={identity}>{children}</EsriIdentityContext.Provider>;
}
